import { ref, type Ref } from 'vue';
import { defineStore } from 'pinia';
import { LoadingStateModel } from '@/models';
import { CaseNoteModel, type PostCaseNote } from '@/models/caseNote';
import CaseNoteService from '@/services/caseNoteService';

type CaseNoteStoreShape = {
  notes: Ref<CaseNoteModel[]>;
  nmpStartTime: Ref<string | undefined>;
  notesLoader: Ref<LoadingStateModel>;
  noteActionLoader: Ref<LoadingStateModel>;
  pinnedPanelOwnerId: Ref<string | null>;
  pinnedPanelCaseId: Ref<string | null>;
  pinnedPanelCaseLabel: Ref<string>;
  pinnedPanelRequestVersion: Ref<number>;
  getNotes: (caseId: string) => Promise<void>;
  createNote: (
    caseId: string,
    note: PostCaseNote
  ) => Promise<CaseNoteModel | undefined>;
  updateNote: (
    caseId: string,
    noteId: string,
    note: PostCaseNote
  ) => Promise<CaseNoteModel | undefined>;
  clearNotes: () => void;
  registerPinnedPanel: (
    ownerId: string,
    caseId: string,
    caseLabel: string
  ) => void;
  unregisterPinnedPanel: (ownerId: string) => void;
  requestPinnedPanelCase: (caseId: string, caseLabel: string) => boolean;
};

export const useCaseNoteStore = defineStore('caseNote', (): CaseNoteStoreShape => {
  const activeCaseId = ref<string | null>(null);
  const notes = ref<CaseNoteModel[]>([]);
  const nmpStartTime = ref<string>();
  const notesLoader = ref(new LoadingStateModel({ isLoading: false }));
  const noteActionLoader = ref(new LoadingStateModel({ isLoading: false }));
  const pinnedPanelOwnerId = ref<string | null>(null);
  const pinnedPanelCaseId = ref<string | null>(null);
  const pinnedPanelCaseLabel = ref('');
  const pinnedPanelRequestVersion = ref(0);
  let activeNotesRequestId = 0;

  const getNotes = async (caseId: string) => {
    const isCaseSwitch = activeCaseId.value !== caseId;
    activeCaseId.value = caseId;
    if (isCaseSwitch) {
      nmpStartTime.value = undefined;
    }
    const requestId = ++activeNotesRequestId;
    notesLoader.value.startLoading();
    try {
      const fetched = await CaseNoteService.getNotes(caseId);
      if (requestId !== activeNotesRequestId) return;
      notes.value = fetched.notes;
      nmpStartTime.value = fetched.nmpStartTime;
    } catch (error) {
      if (requestId !== activeNotesRequestId) return;
      notesLoader.value.setError(error);
    } finally {
      if (requestId === activeNotesRequestId) {
        notesLoader.value.stopLoadingAndFinish();
      }
    }
  };

  const createNote = async (caseId: string, note: PostCaseNote): Promise<CaseNoteModel | undefined> => {
    noteActionLoader.value.startLoading();
    try {
      const newNote = await CaseNoteService.createNote(caseId, note);
      if (activeCaseId.value === caseId) {
        notes.value.unshift(newNote);
      }
      noteActionLoader.value.setSuccess('Note added successfully');
      return newNote;
    } catch (error) {
      noteActionLoader.value.setError(error);
    } finally {
      noteActionLoader.value.stopLoadingAndFinish();
    }
  };

  const updateNote = async (
    caseId: string,
    noteId: string,
    note: PostCaseNote
  ): Promise<CaseNoteModel | undefined> => {
    noteActionLoader.value.startLoading();
    try {
      const updated = await CaseNoteService.updateNote(caseId, noteId, note);
      if (activeCaseId.value === caseId) {
        const idx = notes.value.findIndex(n => n.id === noteId);
        if (idx !== -1) notes.value[idx] = updated;
      }
      noteActionLoader.value.setSuccess('Note updated successfully');
      return updated;
    } catch (error) {
      noteActionLoader.value.setError(error);
    } finally {
      noteActionLoader.value.stopLoadingAndFinish();
    }
  };

  const clearNotes = () => {
    activeNotesRequestId++;
    activeCaseId.value = null;
    notes.value = [];
    nmpStartTime.value = undefined;
  };

  const registerPinnedPanel = (
    ownerId: string,
    caseId: string,
    caseLabel: string
  ) => {
    pinnedPanelOwnerId.value = ownerId;
    pinnedPanelCaseId.value = caseId;
    pinnedPanelCaseLabel.value = caseLabel;
  };

  const unregisterPinnedPanel = (ownerId: string) => {
    if (pinnedPanelOwnerId.value !== ownerId) {
      return;
    }

    pinnedPanelOwnerId.value = null;
    pinnedPanelCaseId.value = null;
    pinnedPanelCaseLabel.value = '';
  };

  const requestPinnedPanelCase = (caseId: string, caseLabel: string) => {
    if (!pinnedPanelOwnerId.value) {
      return false;
    }

    pinnedPanelCaseId.value = caseId;
    pinnedPanelCaseLabel.value = caseLabel;
    pinnedPanelRequestVersion.value += 1;
    return true;
  };

  return {
    notes,
    nmpStartTime,
    notesLoader,
    noteActionLoader,
    pinnedPanelOwnerId,
    pinnedPanelCaseId,
    pinnedPanelCaseLabel,
    pinnedPanelRequestVersion,
    getNotes,
    createNote,
    updateNote,
    clearNotes,
    registerPinnedPanel,
    unregisterPinnedPanel,
    requestPinnedPanelCase
  };
});
